import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS } from '@nks/shared';
import { Button, Flex, Tag } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatDateTime, orDash } from '../../lib/format';
import { useHasPermission } from '../auth/session';
import {
  CUSTOMER_HISTORY,
  type CustomerCase,
  type CustomerFollowUp,
  type CustomerReminder,
} from './api';
import { HistoryCard } from './HistoryCard';
import { HistorySection } from './HistorySection';

/**
 * The customer's service desk: their cases, the calls still to make and the
 * service reminders (with when each was sent), each from its own list.
 */
export function CasesTab({ customerUid, customerName }: { customerUid: string; customerName: string }): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = useHasPermission(PERMISSIONS.CASE_MANAGE);

  const status = (s: string): string => t(`serviceDesk.status.${s}`, { defaultValue: s });
  const severity = (s: string): string => t(`serviceDesk.severity.${s}`, { defaultValue: s });
  const followUpType = (s: string): string => t(`serviceDesk.followUpType.${s}`, { defaultValue: s });
  const ruleLabel = (s: string): string => t(`serviceDesk.reminderRule.${s}`, { defaultValue: s });
  const reminderStatus = (s: string): string => t(`serviceDesk.reminderStatus.${s}`, { defaultValue: s });
  const bike = (u: CustomerReminder['unit']): string => u.plateNo ?? u.chassisNo;
  const overdue = (c: CustomerCase): boolean => c.breached.response || c.breached.resolve;

  return (
    <Flex vertical gap={24}>
      {canManage && (
        <Flex justify="flex-end">
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => {
              // The cases page owns the New case drawer; hand it the customer.
              const search = new URLSearchParams({ new: '1', customer: customerUid, customerName });
              void navigate(`/cases?${search.toString()}`);
            }}
          >
            {t('serviceDesk.new')}
          </Button>
        </Flex>
      )}

      <HistorySection<CustomerCase>
        list="cases"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.cases(customerUid, cursor)}
        title={t('serviceDesk.tab.casesTitle')}
        empty={t('serviceDesk.tab.casesEmpty')}
        forbidden={t('serviceDesk.tab.casesForbidden')}
        href={(row) => `/cases/${row.uid}`}
        columns={[
          { title: t('serviceDesk.col.caseNo'), dataIndex: 'caseNo', width: 180 },
          { title: t('serviceDesk.col.subject'), dataIndex: 'subject' },
          {
            title: t('serviceDesk.col.status'),
            key: 'status',
            render: (_: unknown, row) => (
              <Flex gap={4} wrap>
                <Tag>{status(row.status)}</Tag>
                {overdue(row) && <Tag color="error">{t('serviceDesk.overdue')}</Tag>}
              </Flex>
            ),
          },
          { title: t('serviceDesk.col.severity'), dataIndex: 'severity', width: 110, render: (v: string) => severity(v) },
          { title: t('serviceDesk.col.owner'), key: 'owner', render: (_: unknown, row) => row.owner.name },
          { title: t('serviceDesk.col.opened'), dataIndex: 'createdAt', width: 130, render: (v: string) => formatDate(v) },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.caseNo}
            status={overdue(row) ? `${status(row.status)} · ${t('serviceDesk.overdue')}` : status(row.status)}
            lines={[row.subject, row.owner.name]}
          />
        )}
      />

      <HistorySection<CustomerFollowUp>
        list="follow-ups"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY['follow-ups'](customerUid, cursor)}
        title={t('serviceDesk.tab.followUpsTitle')}
        empty={t('serviceDesk.tab.followUpsEmpty')}
        forbidden={t('serviceDesk.tab.followUpsForbidden')}
        href={() => null}
        columns={[
          { title: t('serviceDesk.tab.col.type'), dataIndex: 'type', render: (v: string) => followUpType(v) },
          {
            title: t('serviceDesk.tab.col.due'),
            dataIndex: 'dueAt',
            render: (v: string, row) => (
              <Flex gap={4} wrap>
                {formatDateTime(v)}
                {row.overdue && <Tag color="error">{t('serviceDesk.tab.overdue')}</Tag>}
              </Flex>
            ),
          },
          { title: t('serviceDesk.tab.col.owner'), key: 'owner', render: (_: unknown, row) => row.owner.name },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={followUpType(row.type)}
            status={row.overdue ? t('serviceDesk.tab.overdue') : t('serviceDesk.tab.open')}
            lines={[formatDateTime(row.dueAt), row.owner.name]}
          />
        )}
      />

      <HistorySection<CustomerReminder>
        list="reminders"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.reminders(customerUid, cursor)}
        title={t('serviceDesk.tab.remindersTitle')}
        empty={t('serviceDesk.tab.remindersEmpty')}
        forbidden={t('serviceDesk.tab.remindersForbidden')}
        href={() => null}
        columns={[
          { title: t('serviceDesk.tab.col.rule'), dataIndex: 'ruleCode', render: (v: string) => ruleLabel(v) },
          { title: t('serviceDesk.tab.col.bike'), key: 'bike', render: (_: unknown, row) => bike(row.unit) },
          { title: t('serviceDesk.tab.col.due'), dataIndex: 'dueDate', width: 130, render: (v: string) => formatDate(v) },
          {
            title: t('serviceDesk.tab.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{reminderStatus(v)}</Tag>,
          },
          {
            title: t('serviceDesk.tab.col.sent'),
            dataIndex: 'sentAt',
            render: (v: string | null) => (v === null ? t('serviceDesk.tab.notSent') : formatDateTime(v)),
          },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={ruleLabel(row.ruleCode)}
            status={reminderStatus(row.status)}
            lines={[
              bike(row.unit),
              `${formatDate(row.dueDate)} · ${orDash(row.sentAt === null ? t('serviceDesk.tab.notSent') : formatDateTime(row.sentAt))}`,
            ]}
          />
        )}
      />

    </Flex>
  );
}
